import produce, { Draft } from 'immer';
import { cloneDeep } from 'lodash';
import { getType } from 'typesafe-actions';

import { Action } from '../../actions';
import { batchesListReducer, initialBatchesListState } from '../BatchesList/reducers';
import { getHtml5AllSizes, getHtml5SelectedSizes, initialSharedData, Order, SharedData } from '../common/typings';
import { Html5CreativesState } from '../Html5Creatives/typings';

import {
    applySizesFilter,
    cancelCreation,
    changePage,
    createCreative,
    createCreativeSuccess,
    deleteCreative,
    deleteCreativeSuccess,
    hideBusy,
    hideCreativesSelector,
    hideDeleteConfirm,
    hideError,
    loadItems,
    loadItemsSuccess,
    saveName,
    saveNameSuccess,
    saveUploadName,
    setSelection,
    showCreativesSelector,
    showDeleteConfirm,
    showError,
    submit,
    submitSizesSelection,
    toggleSizesSelection,
    upload,
    uploadCancelled,
    uploadFinished
} from './actions';
import { getCurrentCreativesState } from './selectors';
import { Html5State } from './state';
import { Html5Page } from './typings';

export function uploadCreativesInitialState() {
    return {
        name: '',
        creatives: {}
    };
}

export function getInitialHtml5AppState(sharedData: SharedData = initialSharedData): Html5State {
    const allSizes = getHtml5AllSizes(sharedData.productType);
    const selectedSizes = getHtml5SelectedSizes(sharedData.filters);
    const isFilterEnabled = sharedData.filters === undefined;

    const filter = {
        order: Order.DESC,
        exportUrl: '',
        isFilterEnabled,
        filterText: '',
        creativeSizes: selectedSizes,
        creativeSizesDataProvider: allSizes
    };

    return {
        page: Html5Page.ACTIVE,
        error: undefined,
        isBusy: false,
        activeCreatives: {
            needRefresh: true,
            batchesList: initialBatchesListState,
            filter
        },
        archiveCreatives: {
            needRefresh: true,
            batchesList: initialBatchesListState,
            filter
        },
        uploadCreatives: uploadCreativesInitialState(),
        productType: sharedData.productType
    };
}

function updateState(draft: Draft<Html5State>, page: Html5Page, creativesState: Partial<Html5CreativesState>): void {
    if (page === Html5Page.ACTIVE) {
        draft.activeCreatives = {
            ...draft.activeCreatives,
            ...creativesState
        };
    } else if (page === Html5Page.ARCHIVE) {
        draft.archiveCreatives = {
            ...draft.archiveCreatives,
            ...creativesState
        };
    }
}

export function html5Reducer(state: Html5State, action: Action): Draft<Html5State> {
    return produce(state, (draft: Draft<Html5State>) => {
        const page = draft.page;
        let creativesState;

        switch (action.type) {
            case getType(changePage):
                draft.page = action.page;
                draft.error = undefined;

                return;

            case getType(upload):
                action.creatives.forEach(creative => {
                    draft.uploadCreatives.creatives[creative.fileId] = creative;
                });

                return;

            case getType(uploadCancelled):
                delete draft.uploadCreatives.creatives[action.creative.fileId];

                return;

            case getType(uploadFinished):
                if (draft.uploadCreatives.creatives.hasOwnProperty(action.creative.fileId)) {
                    draft.uploadCreatives.creatives[action.creative.fileId] = action.creative;
                }

                return;

            case getType(saveUploadName):
                draft.uploadCreatives.name = action.name;

                return;

            case getType(submit):
            case getType(createCreative):
                draft.isBusy = true;

                return;

            case getType(createCreativeSuccess):
                draft.isBusy = false;
                draft.uploadCreatives = uploadCreativesInitialState();
                draft.page = Html5Page.ACTIVE;
                draft.activeCreatives.needRefresh = true;

                return;

            case getType(cancelCreation):
                draft.uploadCreatives = uploadCreativesInitialState();
                draft.page = Html5Page.ACTIVE;

                return;

            case getType(loadItems):
                draft.isBusy = true;
                creativesState = getCurrentCreativesState(draft);
                creativesState.filter.filterText = action.filterText;
                creativesState.filter.order = action.order;
                batchesListReducer(creativesState.batchesList, action);
                updateState(draft, page, creativesState);

                return;

            case getType(hideError):
                draft.error = undefined;

                return;

            case getType(showError):
                draft.isBusy = false;
                draft.error = { message: action.message };

                return;

            case getType(loadItemsSuccess):
            case getType(applySizesFilter):
                draft.isBusy = false;
                creativesState = getCurrentCreativesState(draft);
                creativesState.needRefresh = false;
                creativesState.filter.creativeSizes = action.sizes;

                // если не выбрано размеров в фильтре, значит выбрано всё
                const newAction = cloneDeep(action);
                const allSizes = draft.activeCreatives.filter.creativeSizesDataProvider;
                newAction.sizes = action.sizes.length === 0 ? allSizes : action.sizes;
                batchesListReducer(creativesState.batchesList, newAction);
                updateState(draft, page, creativesState);

                return;

            case getType(deleteCreativeSuccess):
                creativesState = getCurrentCreativesState(draft);
                batchesListReducer(creativesState.batchesList, action);
                updateState(draft, page, creativesState);
                draft.archiveCreatives.needRefresh = true;

                return;

            case getType(hideBusy):
                draft.isBusy = false;

                return;

            case getType(setSelection):
            case getType(saveName):
            case getType(saveNameSuccess):
            case getType(showDeleteConfirm):
            case getType(hideDeleteConfirm):
            case getType(deleteCreative):
            case getType(showCreativesSelector):
            case getType(hideCreativesSelector):
            case getType(toggleSizesSelection):
            case getType(submitSizesSelection): {
                creativesState = getCurrentCreativesState(draft);
                batchesListReducer(creativesState.batchesList, action);
                updateState(draft, page, creativesState);

                return;
            }

            default:
                return;
        }
    });
}
